import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  supplierApi,
  type SupplierRequestStatus,
  type TaskStatus,
} from "@/api/supplierClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/Pagination";
import { useTranslation } from "@/framework-originated/i18n";

type View = "requests" | "tasks";

const TASK_VARIANT: Record<TaskStatus, "default" | "success" | "warning" | "destructive" | "muted"> = {
  staged: "muted",
  pending: "muted",
  dispatching: "warning",
  success: "success",
  failed: "destructive",
  unknown: "warning",
  abandoned: "muted",
};

function taskVariant(status: TaskStatus | "staged") {
  return status === "staged" ? "muted" as const : TASK_VARIANT[status];
}

const REQUEST_VARIANT: Record<SupplierRequestStatus, "success" | "warning" | "destructive"> = {
  processing: "warning",
  success: "success",
  partial_success: "warning",
  failed: "destructive",
};

function fmtTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

export function SupplierRecordsPage() {
  const { t } = useTranslation("supplier");
  const [view, setView] = useState<View>("requests");
  const [requestPage, setRequestPage] = useState(1);
  const [requestStatus, setRequestStatus] = useState<"" | SupplierRequestStatus>("");
  const [selectedRequestID, setSelectedRequestID] = useState<number | null>(null);
  const [taskPage, setTaskPage] = useState(1);
  const [taskStatus, setTaskStatus] = useState<"" | TaskStatus>("");
  const [pageSize, setPageSize] = useState(20);
  const changePageSize = (size: number) => {
    setPageSize(size);
    setRequestPage(1);
    setTaskPage(1);
  };

  const requestsQ = useQuery({
    queryKey: ["supplier", "requests", { page: requestPage, pageSize, status: requestStatus }],
    queryFn: () => supplierApi.listRequests({
      page: requestPage,
      page_size: pageSize,
      status: requestStatus || undefined,
    }),
    enabled: view === "requests",
  });

  const requestDetailQ = useQuery({
    queryKey: ["supplier", "request", selectedRequestID],
    queryFn: () => supplierApi.getRequest(selectedRequestID as number),
    enabled: selectedRequestID != null,
  });

  const tasksQ = useQuery({
    queryKey: ["supplier", "records", { page: taskPage, pageSize, status: taskStatus }],
    queryFn: () => supplierApi.listRecords({
      page: taskPage,
      page_size: pageSize,
      status: taskStatus || undefined,
    }),
    enabled: view === "tasks",
  });

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-primary">Supplier / records</p>
        <h1 className="text-xl font-semibold">{t("records.title")}</h1>
        <p className="mt-0.5 text-xs text-muted-foreground">{t("records.traceDescription")}</p>
      </div>

      <div className="flex gap-1 border-b">
        {(["requests", "tasks"] as View[]).map((item) => (
          <button
            key={item}
            type="button"
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${view === item ? "border-primary font-medium" : "border-transparent text-muted-foreground"}`}
            onClick={() => {
              setView(item);
              setSelectedRequestID(null);
            }}
          >
            {t(`records.tabs.${item}`)}
          </button>
        ))}
      </div>

      {view === "requests" && (
        <RequestList
          query={requestsQ}
          page={requestPage}
          pageSize={pageSize}
          onPageSizeChange={changePageSize}
          status={requestStatus}
          selectedID={selectedRequestID}
          onPage={setRequestPage}
          onStatus={(value) => {
            setRequestStatus(value);
            setRequestPage(1);
            setSelectedRequestID(null);
          }}
          onSelect={setSelectedRequestID}
          t={t}
        />
      )}

      {view === "requests" && selectedRequestID != null && (
        <RequestDetail query={requestDetailQ} t={t} />
      )}

      {view === "tasks" && (
        <TaskList
          query={tasksQ}
          page={taskPage}
          pageSize={pageSize}
          onPageSizeChange={changePageSize}
          status={taskStatus}
          onPage={setTaskPage}
          onStatus={(value) => {
            setTaskStatus(value);
            setTaskPage(1);
          }}
          t={t}
        />
      )}
    </div>
  );
}

type TFunction = (key: string, options?: Record<string, unknown>) => string;

function RequestList({
  query,
  page,
  pageSize,
  onPageSizeChange,
  status,
  selectedID,
  onPage,
  onStatus,
  onSelect,
  t,
}: {
  query: ReturnType<typeof useQuery<Awaited<ReturnType<typeof supplierApi.listRequests>>>>;
  page: number;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  status: "" | SupplierRequestStatus;
  selectedID: number | null;
  onPage: (page: number) => void;
  onStatus: (status: "" | SupplierRequestStatus) => void;
  onSelect: (id: number | null) => void;
  t: TFunction;
}) {
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const statuses: SupplierRequestStatus[] = ["processing", "success", "partial_success", "failed"];

  return (
    <section className="rounded-xl border bg-card shadow-sm">
      <header className="flex items-center gap-2 border-b px-5 py-3">
        <h2 className="text-sm font-semibold">{t("records.requestSection", { total })}</h2>
        <select
          className="ml-auto h-8 rounded-md border bg-background px-2 text-sm"
          value={status}
          onChange={(event) => onStatus(event.target.value as "" | SupplierRequestStatus)}
        >
          <option value="">{t("records.allRequestStatuses")}</option>
          {statuses.map((value) => <option key={value} value={value}>{t(`records.requestStatus.${value}`)}</option>)}
        </select>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/30 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-2">{t("records.requestNo")}</th>
              <th>{t("records.template")}</th>
              <th>{t("records.keyCounts")}</th>
              <th>{t("records.status")}</th>
              <th>{t("records.submittedAt")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className={`border-b last:border-0 ${selectedID === item.id ? "bg-muted/40" : "hover:bg-muted/30"}`}>
                <td className="px-4 py-2 font-mono text-xs">{item.request_no}</td>
                <td>{item.template_name || "—"}</td>
                <td>{item.accepted_key_count}/{item.submitted_key_count}</td>
                <td><Badge variant={REQUEST_VARIANT[item.status]}>{t(`records.requestStatus.${item.status}`)}</Badge></td>
                <td className="text-xs text-muted-foreground">{fmtTime(item.submitted_at)}</td>
                <td className="pr-4 text-right">
                  <Button size="sm" variant="outline" onClick={() => onSelect(selectedID === item.id ? null : item.id)}>
                    {selectedID === item.id ? t("records.closeDetail") : t("records.viewDetail")}
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">{query.isLoading ? "…" : t("records.noRequests")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination className="px-5 pb-3" page={page} total={query.data?.total} pageSize={pageSize}
        onPageChange={onPage} onPageSizeChange={onPageSizeChange} loading={query.isFetching} />
    </section>
  );
}

function RequestDetail({
  query,
  t,
}: {
  query: ReturnType<typeof useQuery<Awaited<ReturnType<typeof supplierApi.getRequest>>>>;
  t: TFunction;
}) {
  const data = query.data;
  return (
    <section className="rounded-xl border bg-card shadow-sm">
      <header className="border-b px-5 py-3">
        <h2 className="text-sm font-semibold">{t("records.requestDetail")}{data ? ` · ${data.request.request_no}` : ""}</h2>
      </header>
      {query.isLoading && <div className="px-5 py-6 text-sm text-muted-foreground">…</div>}
      {query.error && <div className="px-5 py-6 text-sm text-destructive">{String(query.error)}</div>}
      {data && (
        <div className="divide-y">
          {data.items.map((item) => (
            <div key={item.input_index} className="space-y-2 px-5 py-4">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="font-medium">#{item.input_index + 1} · {item.key_name || t("records.unknownKey")}</span>
                {item.key_id != null && <span className="font-mono text-xs text-muted-foreground">Key #{item.key_id}</span>}
                <Badge variant={item.status === "success" ? "success" : item.status === "failed" || item.status === "rejected" ? "destructive" : "warning"}>
                  {t(`records.itemStatus.${item.status}`, { defaultValue: item.status })}
                </Badge>
                {item.safe_error_code && (
                  <span className="text-xs text-destructive">
                    {t(`records.safeError.${item.safe_error_code}`, { defaultValue: item.safe_error_code })}
                  </span>
                )}
              </div>
              {item.tasks.length === 0 ? (
                <div className="text-xs text-muted-foreground">{t("records.noTaskSnapshots")}</div>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/30 text-left text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2">Task</th>
                        <th>{t("records.target")}</th>
                        <th>{t("records.siteType")}</th>
                        <th>{t("records.status")}</th>
                        <th>{t("records.errorCause")}</th>
                        <th>{t("records.completedAt")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.tasks.map((task) => (
                        <tr key={task.task_id} className="border-t">
                          <td className="px-3 py-2">#{task.task_id}</td>
                          <td className="font-medium">{task.target_code}</td>
                          <td>{task.site_type === "push" ? t("records.push") : t("records.relay")}</td>
                          <td><Badge variant={taskVariant(task.status)}>{t(`status.${task.status}`)}</Badge></td>
                          <td className="text-destructive">
                            {task.safe_error_code
                              ? t(`records.safeError.${task.safe_error_code}`, { defaultValue: task.safe_error_code })
                              : "—"}
                          </td>
                          <td className="text-muted-foreground">{fmtTime(task.completed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function TaskList({
  query,
  page,
  pageSize,
  onPageSizeChange,
  status,
  onPage,
  onStatus,
  t,
}: {
  query: ReturnType<typeof useQuery<Awaited<ReturnType<typeof supplierApi.listRecords>>>>;
  page: number;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  status: "" | TaskStatus;
  onPage: (page: number) => void;
  onStatus: (status: "" | TaskStatus) => void;
  t: TFunction;
}) {
  const items = query.data?.items ?? [];
  const statuses = Object.keys(TASK_VARIANT) as TaskStatus[];

  return (
    <section className="rounded-xl border bg-card shadow-sm">
      <header className="flex items-center gap-2 border-b px-5 py-3">
        <h2 className="text-sm font-semibold">{t("records.sectionTitle")}</h2>
        <select className="ml-auto h-8 rounded-md border bg-background px-2 text-sm" value={status} onChange={(event) => onStatus(event.target.value as "" | TaskStatus)}>
          <option value="">{t("records.status")}</option>
          {statuses.map((value) => <option key={value} value={value}>{t(`status.${value}`)}</option>)}
        </select>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/30 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-2">{t("records.keyId")}</th>
              <th>{t("records.target")}</th>
              <th>{t("records.siteType")}</th>
              <th>{t("records.status")}</th>
              <th>{t("records.errorCause")}</th>
              <th>{t("records.createdAt")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="px-4 py-2">#{item.key_id}</td>
                <td className="font-medium">{item.target_code}</td>
                <td>{item.site_type === "push" ? t("records.push") : t("records.relay")}</td>
                <td><Badge variant={TASK_VARIANT[item.status]}>{t(`status.${item.status}`)}</Badge></td>
                <td className="text-muted-foreground">{item.error_message || "—"}</td>
                <td className="text-xs text-muted-foreground">{fmtTime(item.created_at)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">{query.isLoading ? "…" : t("records.empty")}</td></tr>}
          </tbody>
        </table>
      </div>
      <Pagination className="px-5 pb-3" page={page} total={query.data?.total} pageSize={pageSize}
        onPageChange={onPage} onPageSizeChange={onPageSizeChange} loading={query.isFetching} />
    </section>
  );
}
